import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AppSettings {
  // Genel Ayarlar
  siteName: string;
  siteDescription: string;
  siteLogo: string;
  favicon: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  
  // Tema Ayarları
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  boxShadow: string;
  
  // Koyu/Açık Mod Arkaplan Ayarları
  lightModeBackground: string;
  darkModeBackground: string;
  lightModeTextColor: string;
  darkModeTextColor: string;
  lightModeCardBackground: string;
  darkModeCardBackground: string;
  
  // Layout Ayarları
  sidebarWidth: number;
  headerHeight: number;
  footerHeight: number;
  contentPadding: number;
  
  // Dashboard Ayarları
  welcomeMessage: string;
  showWelcomeMessage: boolean;
  showQuickActions: boolean;
  showStatistics: boolean;
  showRecentActivities: boolean;
  
  // Rapor Ayarları
  defaultReportType: string;
  reportRetentionDays: number;
  autoSaveReports: boolean;
  allowBulkDelete: boolean;
  
  // Rapor Türü Ayarları
  callReportSettings: {
    enabled: boolean;
    requiredFields: string[];
    optionalFields: string[];
    customFields: Array<{name: string, type: string, required: boolean}>;
  };
  riskReportSettings: {
    enabled: boolean;
    requiredFields: string[];
    optionalFields: string[];
    customFields: Array<{name: string, type: string, required: boolean}>;
  };
  livechatReportSettings: {
    enabled: boolean;
    requiredFields: string[];
    optionalFields: string[];
    customFields: Array<{name: string, type: string, required: boolean}>;
  };
  
  // Chat Ayarları
  enableChat: boolean;
  allowFileUpload: boolean;
  enableNotifications: boolean;
  maxFileSize: number;
  chatHistoryDays: number;
  
  // Güvenlik Ayarları
  sessionTimeout: number;
  maxLoginAttempts: number;
  passwordMinLength: number;
  enable2FA: boolean;
  requireTwoFactor: boolean;
  requireStrongPassword: boolean;
  passwordRequireSpecial: boolean;
  
  // Performans Ayarları
  enableCaching: boolean;
  enableCompression: boolean;
  cacheExpiration: number;
  maxUploadSize: number;
  
  // Bildirim Ayarları
  enableEmailNotifications: boolean;
  enablePushNotifications: boolean;
  enableSMSNotifications: boolean;
  enableSoundNotifications: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  notificationSound: boolean;
  
  // Dil ve Bölge
  defaultLanguage: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  currency: string;
  
  // Özel Kod
  customCSS: string;
  customJavaScript: string;
  
  // Menü Ayarları
  menuItems: Array<{
    id: string;
    title: string;
    icon: string;
    path: string;
    visible: boolean;
    order: number;
  }>;
  
  // Data Bağlantısı Ayarları
  dataConnection: {
    type: 'none' | 'mongodb' | 'googlesheets';
    mongodb: {
      connectionString: string;
      databaseName: string;
      isConnected: boolean;
    };
    googlesheets: {
      spreadsheetId: string;
      credentials: string;
      isConnected: boolean;
    };
  };
}

const defaultSettings: AppSettings = {
  siteName: 'MR Gamb Panel',
  siteDescription: 'Gelişmiş CRM ve Rapor Yönetim Sistemi',
  siteLogo: '',
  favicon: '',
  maintenanceMode: false,
  maintenanceMessage: 'Sistem bakımda. Lütfen daha sonra tekrar deneyin.',
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  backgroundColor: '#ffffff',
  textColor: '#000000',
  borderRadius: 8,
  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
  lightModeBackground: '#ffffff',
  darkModeBackground: '#121212',
  lightModeTextColor: '#000000',
  darkModeTextColor: '#ffffff',
  lightModeCardBackground: '#ffffff',
  darkModeCardBackground: '#1e1e1e',
  sidebarWidth: 280,
  headerHeight: 64,
  footerHeight: 48,
  contentPadding: 17,
  welcomeMessage: 'Hoş geldiniz!',
  showWelcomeMessage: true,
  showQuickActions: true,
  showStatistics: true,
  showRecentActivities: true,
  defaultReportType: 'daily',
  reportRetentionDays: 365,
  autoSaveReports: true,
  allowBulkDelete: true,
  
  // Rapor Türü Ayarları - Varsayılan değerler
  callReportSettings: {
    enabled: true,
    requiredFields: ['customerName', 'phoneNumber', 'callDuration', 'callResult'],
    optionalFields: ['notes', 'followUpDate', 'priority'],
    customFields: [
      { name: 'Müşteri Memnuniyeti', type: 'rating', required: false },
      { name: 'Tekrar Aranacak', type: 'boolean', required: false }
    ]
  },
  riskReportSettings: {
    enabled: true,
    requiredFields: ['transactionType', 'amount', 'riskLevel', 'status'],
    optionalFields: ['notes', 'reviewer', 'approvalDate'],
    customFields: [
      { name: 'Risk Skoru', type: 'number', required: true },
      { name: 'Onay Durumu', type: 'select', required: true }
    ]
  },
  livechatReportSettings: {
    enabled: true,
    requiredFields: ['customerName', 'chatDuration', 'resolution', 'satisfaction'],
    optionalFields: ['notes', 'followUpRequired', 'tags'],
    customFields: [
      { name: 'Müşteri Puanı', type: 'rating', required: true },
      { name: 'Çözüm Süresi', type: 'number', required: false }
    ]
  },
  enableChat: true,
  allowFileUpload: true,
  enableNotifications: true,
  maxFileSize: 10,
  chatHistoryDays: 30,
  sessionTimeout: 30,
  maxLoginAttempts: 5,
  passwordMinLength: 8,
  enable2FA: false,
  requireTwoFactor: false,
  requireStrongPassword: true,
  passwordRequireSpecial: true,
  enableCaching: true,
  enableCompression: true,
  cacheExpiration: 3600,
  maxUploadSize: 50,
  enableEmailNotifications: true,
  enablePushNotifications: true,
  enableSMSNotifications: false,
  enableSoundNotifications: true,
  emailNotifications: true,
  pushNotifications: true,
  smsNotifications: false,
  notificationSound: true,
  defaultLanguage: 'tr',
  timezone: 'Europe/Istanbul',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24',
  currency: 'TRY',
  customCSS: '',
  customJavaScript: '',
  menuItems: [],
  
  // Data Bağlantısı - Varsayılan değerler
  dataConnection: {
    type: 'none' as const,
    mongodb: {
      connectionString: '',
      databaseName: '',
      isConnected: false
    },
    googlesheets: {
      spreadsheetId: '',
      credentials: '',
      isConnected: false
    }
  }
};

interface SettingsContextType {
  settings: AppSettings;
  updateSetting: (key: keyof AppSettings | string, value: any) => void;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  resetSettings: () => void;
  saveSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(() => {
    const savedSettings = localStorage.getItem('appSettings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });

  // Settings değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = (key: keyof AppSettings | string, value: any) => {
    setSettings(prev => {
      // Nested path kontrolü
      if (typeof key === 'string' && key.includes('.')) {
        const keys = key.split('.');
        const lastKey = keys.pop()!;
        let current: any = prev;
        
        // Nested objeye git
        for (const k of keys) {
          if (!current[k]) current[k] = {};
          current = current[k];
        }
        
        // Eğer değer aynıysa güncelleme yapma
        if (current[lastKey] === value) {
          return prev;
        }
        
        // Nested güncelleme
        const newSettings = { ...prev };
        let target: any = newSettings;
        for (const k of keys) {
          target[k] = { ...target[k] };
          target = target[k];
        }
        target[lastKey] = value;
        
        return newSettings;
      } else {
        // Normal güncelleme
        const typedKey = key as keyof AppSettings;
        if (prev[typedKey] === value) {
          return prev;
        }
        return { ...prev, [typedKey]: value };
      }
    });
  };

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings(prev => {
      const updatedSettings = { ...prev, ...newSettings };
      return updatedSettings;
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const saveSettings = () => {
    // Zaten useEffect ile otomatik kaydediliyor, bu fonksiyon sadece UI feedback için
    console.log('Settings saved manually:', settings);
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSetting,
      updateSettings,
      resetSettings,
      saveSettings
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
